// Messaging API Service for Chatbot Integration
// This connects to the counsellor portal messaging system

// API base URL for messaging endpoints
// Production server

const API_BASE_URL = import.meta.env.VITE_COUNSELLOR_API_BASE_URL;

/**
 * Create a new conversation between customer and doctor
 * @param {number} customerId - Customer/User ID
 * @param {number} doctorId - Doctor/Counsellor ID
 * @param {number|null} appointmentId - Optional appointment ID
 */
export const createConversation = async (customerId, doctorId, appointmentId = null, appName = "echoshine") => {
  try {
    const response = await fetch(`${API_BASE_URL}/messaging/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: customerId,
        doctor_id: doctorId,
        appointment_id: appointmentId,
        app_name: appName,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create conversation');
    }

    return response.json();
  } catch (error) {
    console.error('Error creating conversation:', error);
    throw error;
  }
};

/**
 * Send a message in a conversation
 * @param {number} conversationId - Conversation ID
 * @param {string} messageText - Message content
 * @param {number} customerId - Customer ID (sender)
 */
export const sendCustomerMessage = async (conversationId, messageText, customerId, senderName = '') => {
  try {
    const response = await fetch(`${API_BASE_URL}/messaging/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversation_id: conversationId,
        sender_type: 'customer',
        sender_id: customerId,
        sender_name: senderName,
        message_text: messageText,
        message_type: 'text',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to send message');
    }

    return response.json();
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Get messages for a conversation
 * @param {number} conversationId - Conversation ID
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Messages per page (default: 50)
 */
export const getMessages = async (conversationId, page = 1, limit = 50) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messaging/messages/${conversationId}?page=${page}&limit=${limit}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch messages');
    }

    const data = await response.json();
    // Reverse to show oldest first
    return {
      ...data,
      messages: (data.messages || []).reverse()
    };
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

/**
 * Mark messages as read
 * @param {number} conversationId - Conversation ID
 */
export const markMessagesAsRead = async (conversationId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messaging/messages/${conversationId}/read?user_type=customer`,
      { method: 'PUT' }
    );

    if (!response.ok) {
      throw new Error('Failed to mark messages as read');
    }

    return response.json();
  } catch (error) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
};

/**
 * Get conversations for a customer
 * @param {number} customerId - Customer ID
 */
export const getCustomerConversations = async (customerId, appName = "echoshine") => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messaging/conversations?user_type=customer&user_id=${customerId}&app_name=${appName}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch conversations');
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

/**
 * Get unread message count for customer
 * @param {number} customerId - Customer ID
 */
export const getUnreadCount = async (customerId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/messaging/unread-count?user_type=customer&user_id=${customerId}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch unread count');
    }

    const data = await response.json();
    return data.unread_count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
};
